import { CallBtn } from '@/buttons';
import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
// eslint-disable-next-line
import { useLocation } from '@reach/router';
import { Fragment, useEffect, useRef, useState } from 'react';
import { siteInfo } from './NavLinks';

const BAR_HEIGHT = 76;
const SCROLL_OFFSET = 300; // px scrolled before the bar slides in

// anchored at the top of the document; the observer's rootMargin, not this
// element's own offset, is what sets the distance we trigger at
const Sentinel = styled.span`
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
`;

const Bar = styled.div<{ visible: boolean }>`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background-color: ${({ theme }) => theme.colors.dark};
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.3);
    transform: translateY(${({ visible }) => (visible ? '0' : '100%')});
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
    transition: transform 0.3s ease-in-out, visibility 0.3s;

    > a {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        box-shadow: none;
    }

    @media (min-width: 768px) {
        display: none;
    }
`;

// keeps the footer from sitting underneath the fixed bar
const mobileSpacing = css`
    @media (max-width: 767px) {
        body {
            padding-bottom: calc(${BAR_HEIGHT}px + env(safe-area-inset-bottom));
        }
    }
`;

export const StickyCallBar = () => {
    const { pathname } = useLocation();
    const sentinelRef = useRef<HTMLSpanElement>(null);

    // starts hidden so the server-rendered markup never paints the bar before
    // the observers have reported
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const sentinel = sentinelRef.current;

        if (!sentinel || typeof window.IntersectionObserver !== 'function') return;

        // navigating resets the scroll, so don't carry the last page's state over
        setVisible(false);

        // a page's own call-to-action (e.g. the hero) is marked with data-call-cta,
        // so the bar doesn't duplicate it while it's on screen
        const cta = document.querySelector('[data-call-cta]');

        // each stays undefined until its observer first reports. Treating "not
        // measured yet" as false is what used to show the bar on load, so the
        // bar waits until both are actually known.
        let scrolledPast: boolean | undefined;
        let ctaInView: boolean | undefined = cta ? undefined : false;

        const update = () => {
            if (scrolledPast === undefined || ctaInView === undefined) return;
            setVisible(scrolledPast && !ctaInView);
        };

        // growing the root upwards by SCROLL_OFFSET keeps the sentinel intersecting
        // until the page has scrolled that far, so no scroll handler is needed
        const scrollObserver = new IntersectionObserver(
            ([entry]) => {
                scrolledPast = !entry.isIntersecting;
                update();
            },
            { threshold: 0, rootMargin: `${SCROLL_OFFSET}px 0px 0px 0px` }
        );
        scrollObserver.observe(sentinel);

        let ctaObserver: IntersectionObserver | undefined;

        if (cta) {
            ctaObserver = new IntersectionObserver(
                ([entry]) => {
                    ctaInView = entry.isIntersecting;
                    update();
                },
                { threshold: 0 }
            );
            ctaObserver.observe(cta);
        }

        return () => {
            scrollObserver.disconnect();
            ctaObserver?.disconnect();
        };
    }, [pathname]);

    return (
        <Fragment>
            <Global styles={mobileSpacing} />
            <Sentinel ref={sentinelRef} aria-hidden="true" />
            <Bar visible={visible}>
                <CallBtn>Emergency? Call {siteInfo.phone.text}</CallBtn>
            </Bar>
        </Fragment>
    );
};
